import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { JobDtoType, LongRunningJob } from '../index';
import { JobConfiguration } from '../job-configuration';
import { JobType } from '../../../api/jobs.dao';
import { IAPIErrorObject, IErrorParsing } from '../../../infrastructure/error-parsing';
import { registerJobCompletionHandler } from './execution-handlers-registry';
import { JobTriggerRunner } from './job-trigger-runner';
import { EventExecutionRuntime } from './event-execution-runtime';
import { JobResultHandler } from './job-result-handler';
import { JobTrackingService } from './job-tracking.service';


export class Job implements LongRunningJob {
    public static configure(jobConfiguration, executionRuntime): Job {
        return new Job(jobConfiguration, executionRuntime);
    }

    private assignJobIdToHandler?: (id: number) => void;

    private get runtime(): EventExecutionRuntime { return this.executionRuntime; }
    private get job(): JobConfiguration { return this.config; }

    private jobResultHandler: JobResultHandler = new JobResultHandler();

    private constructor(
        private readonly config: JobConfiguration,
        private readonly executionRuntime: EventExecutionRuntime,
        private readonly errorParsing: IErrorParsing = IoC.Container.get(IErrorParsing),
        private readonly jobTrackingService: JobTrackingService = IoC.Container.get(JobTrackingService)
    ) {}

    public get type(): JobType { return this.job.type; }

    /**
     * Check if the Job can be triggered. Can be used for disabling buttons, .etc.
     */
    public get triggerEnabled() {
        return this.runtime.isReady();
    }

    @mobx.action.bound
    private processTriggerResult(result: JobDtoType | IAPIErrorObject, additionalProps?: { description: string}): void {
        const triggerRunner = JobTriggerRunner.create(this.type, result);
        this.jobResultHandler.handleJobTriggerResult(triggerRunner, this.runtime.stopProcessing, additionalProps);
        this.trackTriggeredResult(triggerRunner);
    }

    private trackTriggeredResult(jobTrigger: JobTriggerRunner): void {
        if (jobTrigger.isTriggered()) this.jobTrackingService.trackJobTriggerResult(jobTrigger);
    }

    /**
     * Register callback to be called after the job execution is finished ("Completed" or "Failed").
     */
    public onComplete = (cb: () => void): void => {
        this.assignJobIdToHandler = registerJobCompletionHandler(cb);
    };

    /**
     * Trigger Job execution.
     */
    @mobx.action
    public async trigger(params: object = {}, additionalProps?: { description: string}): Promise<void> {
        if (!this.triggerEnabled) return;

        try {
            this.runtime.startProcessing();
            const triggerResult = await this.job.execute({ ...this.runtime.getParams(), ...params });
            if (this.assignJobIdToHandler) this.assignJobIdToHandler(triggerResult.jobId);
            this.runtime.addExecutionToState(triggerResult);
            this.processTriggerResult(triggerResult, additionalProps);
        } catch (error) {
            const triggerError = this.errorParsing.extractAPIError(error);
            this.processTriggerResult(triggerError);
            throw error;
        } finally {
            await this.runtime.revalidateExecutionState();
        }
    }
}
